import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import T from 'prop-types'
import * as yup from 'yup'
import logger from 'logger'
import {
  AGENDA_STATE_CLOSED,
  AGENDA_STATE_OPEN,
  AGENDA_TASK_BLIND_PUBLICATION,
  AGENDA_TASKS,
  AGENDA_TASK_CREATION,
} from 'lib/agenda'
import { format } from 'date-fns'
import itLocale from 'date-fns/locale/it'

import SessionsOption from '../Sessions/SessionsOption'

import TaskDatesRow from './TaskDatesRow'

const GridWrapper = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 2fr',
  columnGap: theme.spacing(3),
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
}))

export default function SessionsPlanning({
  session,
  updateDate,
  handleValidation,
  handleConfirm,
  handleBeforeAssignmentConfirm,
  handleShowOtherPartners,
  assignment,
  readOnly,
  contentIsReadOnly = false,
}) {
  const { t } = useTranslation()

  const [sessionState, setSessionState] = useState(session)

  const [datesErrors, setDatesErrors] = useState([])

  useEffect(() => {
    setSessionState(session)
  }, [session])

  const preparation = useMemo(
    () => session.agenda.sessionCreation.state === AGENDA_STATE_OPEN,
    [session.agenda.sessionCreation.state]
  )

  useEffect(() => {
    try {
      const errors = handleValidation(sessionState)

      if (errors.length) {
        setDatesErrors(errors)
      }
    } catch (e) {
      logger.error(Object.entries(e))
    }
  }, [handleValidation, sessionState, setDatesErrors, t])

  const isValid = useMemo(
    () => handleValidation(sessionState).length === 0,
    [handleValidation, sessionState]
  )

  const canConfirmDate = useMemo(() => {
    return (
      (preparation && !sessionState.planningAcceptedAt) ||
      (sessionState.confirmationAcceptedAt && !sessionState.assignmentStartedAt)
    )
  }, [preparation, sessionState])

  const agendaTasks = useMemo(
    () =>
      AGENDA_TASKS.filter(
        (task) =>
          task !== AGENDA_TASK_BLIND_PUBLICATION && sessionState?.agenda?.[task]
      ),
    [sessionState?.agenda]
  )

  const handleDateChange = async (newDate, id) => {
    setDatesErrors([])
    const [task, attribute] = id.split('.')

    setSessionState((s) => ({
      ...s,
      agenda: {
        ...s.agenda,
        [task]: { ...s.agenda[task], [attribute]: newDate },
      },
    }))
    if (yup.date().isValidSync(newDate)) {
      session.agenda.sessionCreation.state !== AGENDA_STATE_CLOSED &&
        (await updateDate(
          sessionState.sessionId,
          sessionState.agenda[task].sessionAgendaId,
          attribute.replace('sAt', ''),
          newDate
        ))
    }
  }

  const handleLocalConfirm = () => {
    preparation
      ? handleConfirm()
      : handleBeforeAssignmentConfirm(session.sessionId, {
          publicationStart: sessionState.agenda.publication.startsAt,
          publicationEnd: sessionState.agenda.publication.endsAt,
          blindPublicationStart: sessionState.agenda.publication.startsAt,
          blindPublicationEnd: sessionState.agenda.publication.endsAt,
          assignmentStart: sessionState.agenda.assignment.startsAt,
          assignmentEnd: sessionState.agenda.assignment.endsAt,
        })
  }

  return (
    <div data-testid="session-planning">
      <GridWrapper>
        <div>
          <Typography variant="body2" color="textSecondary">
            <strong>{t('sessions.sessionId')}</strong>
          </Typography>
          <Typography variant="body2" color="textPrimary">
            <strong>{sessionState.sessionId}</strong>
          </Typography>
        </div>
        <div>
          <Typography variant="body2" color="textSecondary">
            <strong>{t('sessions.planning.status')}</strong>
          </Typography>
          <Typography variant="body2" color={preparation ? 'primary' : 'error'}>
            <strong>
              {t(
                `sessions.state.sessionCreation.${sessionState.agenda.sessionCreation.state}`
              )}
            </strong>
          </Typography>
        </div>
        <div style={{ textAlign: 'right' }}>
          <FormControlLabel
            control={
              <Switch
                data-testid="show-other-partners"
                disabled={!preparation || readOnly || contentIsReadOnly}
                checked={session.showOtherPartners}
                onChange={(e) =>
                  handleShowOtherPartners(session.sessionId, e.target.checked)
                }
              />
            }
            label={
              <SessionsOption
                label={t('sessions.options.titles.choice')}
                value={t(
                  `sessions.options.${session.showOtherPartners ? 'on' : 'off'}`
                )}
              />
            }
            labelPlacement="start"
            sx={{ mr: 0 }}
          />
          <Button
            data-testid="button-confirm"
            color="primary"
            variant="contained"
            disabled={
              !isValid ||
              assignment ||
              readOnly ||
              contentIsReadOnly ||
              !canConfirmDate
            }
            onClick={handleLocalConfirm}
          >
            {t('sessions.planning.confirmButton')}
          </Button>
        </div>
      </GridWrapper>
      <Divider light />
      {agendaTasks.map((task) => {
        return (
          <TaskDatesRow
            key={task}
            agenda={sessionState.agenda}
            task={sessionState.agenda[task]}
            tasksOrder={agendaTasks}
            errors={datesErrors}
            handleDateChange={handleDateChange}
            readOnly={
              assignment ||
              (task === AGENDA_TASK_CREATION &&
                session.agenda.sessionCreation.state === 'closed') ||
              readOnly ||
              contentIsReadOnly
            }
          />
        )
      })}
      {!!session?.availabilityAt && (
        <>
          <Box pb={3} />
          <Typography>
            <Typography component="span" variant="body2" color="textSecondary">
              <strong>{t('sessions.planning.availabilityAt')}</strong>
            </Typography>
            <Typography component="span" variant="body2" color="textPrimary">
              <strong>
                {format(
                  new Date(session.availabilityAt),
                  t('common.date.formatDMY'),
                  {
                    locale: itLocale,
                  }
                )}
              </strong>
            </Typography>
          </Typography>
        </>
      )}
    </div>
  )
}

SessionsPlanning.propTypes = {
  session: T.object.isRequired,
  updateDate: T.func.isRequired,
  handleValidation: T.func.isRequired,
  handleConfirm: T.func.isRequired,
  handleBeforeAssignmentConfirm: T.func.isRequired,
  handleShowOtherPartners: T.func.isRequired,
  assignment: T.bool,
  readOnly: T.bool,
  contentIsReadOnly: T.bool,
}
